import styles from './Button.module.scss'

/**
 * Universal button component.
 *
 * Children are rendered directly — pass icon SVG components or text as children.
 * For icon + label combos, compose inside a wrapper (no .Icon subcomponent needed).
 *
 * @param {object}                  props
 * @param {'button'|'submit'|'reset'} [props.type='button'] - native button type
 * @param {'icon'|'text'|'wide'}      [props.kind='icon']   - layout mode: square, auto-width, or full-width
 * @param {'xsmall'|'small'|'medium'|'large'|'xlarge'} [props.size='large'] - geometry scale
 * @param {boolean}                 [props.selected=false] - secondary-colour background (toggle state)
 * @param {boolean}                 [props.cta=false]      - primary-colour background (call-to-action)
 * @param {boolean}                 [props.disabled=false]      
 * @param {'default'|'ghost'}       [props.variant='default'] - ghost = transparent bg + border
 * @param {string}                  [props.className]      - extra class appended to root
 * @param {React.ReactNode}         props.children
 * @param {...*}                    rest - forwarded to <button> (onClick, disabled, aria-*…)
 */
export function Button({
  type = 'button',
  kind = 'icon',
  size = 'large',
  selected = false,
  cta = false,
  disabled = false,
  variant = 'default',
  className,
  children,
  ...rest
}) {
  const kindClass = {
    icon: styles.kindIcon,
    text: styles.kindText,
    wide: styles.kindWide,
  }[kind] ?? styles.kindIcon

  const sizeClass = {
    xsmall: styles.sizeXSmall,
    small: styles.sizeSmall,
    medium: styles.sizeMedium,
    large: styles.sizeLarge,
    xlarge: styles.sizeXLarge,
  }[size] ?? styles.sizeLarge

  const classes = [
    sizeClass,
    kindClass,
    selected ? styles.selected : null,
    cta     ? styles.cta      : null,
    variant === 'ghost' ? styles.ghost : null,
    className,
  ].filter(Boolean).join(' ')

  return (
    <button type={type} className={classes} disabled={disabled} {...rest}>
      {children}
    </button>
  )
}
